import React, { useEffect, useState, useRef, useMemo } from 'react';
import { scrollToSection } from '../utils/scrollToSection';
import { prefersReducedMotion } from '../hooks';

// Organized greetings by time periods
const greetingsData = [
  // MORNING (5am - 12pm)
  { text: "Gud mawnin", lang: "Krio", meaning: "Good morning", period: "morning" },
  { text: "Kushɛ-o", lang: "Krio", meaning: "Hello", period: "morning" },
  { text: "Sekɛ", lang: "Temne", meaning: "Welcome/Morning", period: "morning" },
  { text: "Kɔri-o", lang: "Temne", meaning: "How are you?", period: "morning" },
  
  // AFTERNOON (12pm - 5pm)
  { text: "Gud aftanun", lang: "Krio", meaning: "Good afternoon", period: "afternoon" },
  { text: "How de bɔdi?", lang: "Krio", meaning: "How's the body?", period: "afternoon" },
  { text: "Sekɛ m'pa", lang: "Temne", meaning: "Good afternoon", period: "afternoon" },
  
  // EVENING/NIGHT (5pm - 4am)
  { text: "Gud ivnin", lang: "Krio", meaning: "Good evening", period: "evening" },
  { text: "Kushɛ-o", lang: "Krio", meaning: "Hello", period: "evening" },
  { text: "Sekɛ m'fana", lang: "Temne", meaning: "Good evening", period: "evening" },
];

const INTRO_SEEN_KEY = 'introSeen';
const START_SCALE = 1.5;

// Freetown and Accra are both on GMT, so the clock shows UTC rather than the
// visitor's local time. Its own component so the tick doesn't re-render the hero.
const GmtClock = () => {
  const format = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  const [time, setTime] = useState(format);
  useEffect(() => {
    const id = setInterval(() => setTime(format()), 15000);
    return () => clearInterval(id);
  }, []);
  return <>{time} GMT</>;
};

// Mobile/tablet hero: the role under the name cycles through the three.
const ROLES = ['Engineer', 'Advocate', 'Designer'];
const RoleCycle = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % ROLES.length), 2400);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="relative inline-block overflow-hidden align-bottom h-[1.25em]">
      <span
        key={i}
        className="block"
        style={{ animation: 'slideUp 0.7s cubic-bezier(0.19, 1, 0.22, 1)' }}
      >
        {ROLES[i]}<span className="text-[#ff6347]">.</span>
      </span>
    </span>
  );
};

export const HeroSection = () => {
  const hasSeenIntro = typeof window !== 'undefined' && (sessionStorage.getItem(INTRO_SEEN_KEY) === 'true' || prefersReducedMotion());

  const [scrollY, setScrollY] = useState(0);
  const [targetData, setTargetData] = useState(null);
  const [isMeasurementReady, setIsMeasurementReady] = useState(false);
  const fallingNameRef = useRef(null);
  const [isIntroComplete, setIsIntroComplete] = useState(hasSeenIntro);
  const [loadStage, setLoadStage] = useState(hasSeenIntro ? 'settled' : 'initial');
  const [isAppLoaded, setIsAppLoaded] = useState(hasSeenIntro);
  const [showSkip, setShowSkip] = useState(!hasSeenIntro);
  const [isIntroDaveroseActive, setIsIntroDaveroseActive] = useState(!hasSeenIntro);
  const [isFastMode, setIsFastMode] = useState(false);
  const [index, setIndex] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // The mobile intro overlay fades out (rather than vanishing) while the
  // hero name slides up underneath it; removed once the fade finishes.
  const [isMobileOverlayGone, setIsMobileOverlayGone] = useState(hasSeenIntro);
  const timersRef = useRef({});

  const currentGreetings = useMemo(() => {
    const hour = new Date().getHours();
    let period = "evening";
    if (hour >= 0 && hour < 12) period = "morning";
    else if (hour >= 12 && hour < 17) period = "afternoon";
    return greetingsData.filter(g => g.period === period);
  }, []);

  const expoOut = "cubic-bezier(0.19, 1, 0.22, 1)";
  // Decided once on load: the intro is either the mobile overlay or the desktop
  // split-name sequence, and switching mid-animation would break it.
  const [isMobileOrTablet] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);

  const maxScroll = targetData ? targetData.animationEndScroll : 1000;
  const rawProgress = isMeasurementReady && maxScroll > 0 ? scrollY / maxScroll : 0;
  const movementProgress = Math.min(Math.max(rawProgress, 0), 1.0);

  // The falling name only shows once the desktop intro has handed over to it.
  const nameReady = !isMobileOrTablet && loadStage === 'settled' && isIntroComplete && !isIntroDaveroseActive;
  const nameReadyRef = useRef(nameReady);
  nameReadyRef.current = nameReady;
  const positionNameRef = useRef(null);

  useEffect(() => {
    // 1. Capture the ref in a variable at the very start
    const currentTimers = timersRef.current;

    if (!hasSeenIntro) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.body.style.overflow = 'hidden';

      currentTimers.splitTimer = setTimeout(() => setLoadStage('splitting'), 400);
      currentTimers.settledTimer = setTimeout(() => setLoadStage('settled'), 2000);
    }

    const measureTarget = () => {
      const targetEl = document.getElementById('name-destination');
      const fallingName = fallingNameRef.current;
      if (targetEl && fallingName) {
        const tRect = targetEl.getBoundingClientRect();
        const targetAbsoluteTop = tRect.top + window.scrollY;
        const targetAbsoluteCenterY = targetAbsoluteTop + (tRect.height / 2);
        const viewportCenterY = window.innerHeight / 2;
        const animationEndScroll = targetAbsoluteCenterY - viewportCenterY;
        setTargetData({ animationEndScroll });
        setIsMeasurementReady(true);
      }
    };

    if (isMobileOrTablet) {
      if (!hasSeenIntro) {
        currentTimers.mobileComplete = setTimeout(() => {
          setLoadStage('settled');
          setIsAppLoaded(true);
          setIsIntroComplete(true);
          setIsIntroDaveroseActive(false);
          setShowSkip(false);
          document.body.style.overflow = 'auto';
          sessionStorage.setItem(INTRO_SEEN_KEY, 'true');
        }, 3500);
      }
    } else {
    // Only set desktop-specific timeouts/listeners if NOT mobile
      setTimeout(measureTarget, 100);
      setTimeout(measureTarget, 2100);
    }

    let rafId = null;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setScrollY(window.scrollY);
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', measureTarget);

    // 2. Single, unified cleanup function
    return () => {
      if (currentTimers.splitTimer) clearTimeout(currentTimers.splitTimer);
      if (currentTimers.settledTimer) clearTimeout(currentTimers.settledTimer);
      if (currentTimers.mobileComplete) clearTimeout(currentTimers.mobileComplete);

      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', measureTarget);
      if (rafId !== null) cancelAnimationFrame(rafId);
      document.body.style.overflow = '';
    };
  }, [isMobileOrTablet]);

  // Fly the hero name down into "I'm Daverose Tholley." in the About heading.
  // Positioned straight from the scroll handler rather than through React
  // state, so it moves in the same frame as the page and the hand-off to the
  // real heading is pixel-exact. Everything is measured live, so late layout
  // shifts (fonts, images) can't knock it out of line.
  useEffect(() => {
    if (isMobileOrTablet) return;

    const position = () => {
      const falling = fallingNameRef.current;
      const target = document.getElementById('name-destination');
      if (!falling || !target) return;

      const t = target.getBoundingClientRect();
      // clientWidth excludes the scrollbar, matching what `fixed; left: 50%` uses.
      const viewportCentreX = document.documentElement.clientWidth / 2;
      const endScroll = window.scrollY + (t.top + t.height / 2) - window.innerHeight / 2;
      const landed = window.scrollY >= endScroll - 0.5;
      const p = endScroll > 0 ? Math.min(Math.max(window.scrollY / endScroll, 0), 1) : 1;

      const moveX = (t.left + t.width / 2) - viewportCentreX;
      // offsetWidth ignores transforms, so this ratio is stable.
      const endScale = falling.offsetWidth > 0 ? target.offsetWidth / falling.offsetWidth : 1;
      const scale = START_SCALE - p * (START_SCALE - endScale);

      falling.style.transform = `translate(-50%, -50%) translateX(${p * moveX}px) scale(${scale})`;
      falling.style.opacity = nameReadyRef.current && !landed ? '1' : '0';
      target.style.opacity = landed ? '1' : '0';
    };

    positionNameRef.current = position;
    position();
    window.addEventListener('scroll', position, { passive: true });
    window.addEventListener('resize', position);
    document.fonts?.ready.then(position);
    return () => {
      window.removeEventListener('scroll', position);
      window.removeEventListener('resize', position);
      positionNameRef.current = null;
    };
  }, [isMobileOrTablet]);

  useEffect(() => {
    positionNameRef.current?.();
  }, [nameReady]);

  useEffect(() => {
    if (loadStage === 'settled' && !isIntroDaveroseActive && !isMobileOrTablet) {
        setIsAppLoaded(true);
        setIsIntroComplete(true);
        setShowSkip(false);
        document.body.style.overflow = 'auto';
        sessionStorage.setItem(INTRO_SEEN_KEY, 'true');
    }
  }, [loadStage, isIntroDaveroseActive, isMobileOrTablet]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % currentGreetings.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [currentGreetings.length]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKey = (e) => e.key === 'Escape' && setIsMobileMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileMenuOpen]);

  const skipIntro = () => {
    setIsFastMode(true);
    setShowSkip(false);
    setLoadStage('settled');
    setTimeout(() => { setIsIntroDaveroseActive(false); }, 600);
  };

  return (
    <>
      {!isAppLoaded && <div className="fixed inset-0 z-[20000] bg-transparent cursor-wait" />}
      
      {showSkip && !isMobileOrTablet && (
        <button onClick={skipIntro} className="fixed bottom-8 right-8 z-[20001] text-[11px] tracking-[4px] uppercase text-charcoal/70 hover:text-charcoal transition-all duration-300 font-sans px-4 py-2 border border-charcoal/30 hover:border-charcoal/60 rounded-sm backdrop-blur-sm bg-white/30 hover:bg-white/50">
          Skip Intro →
        </button>
      )}

      {/* MOBILE ONLY INTRO OVERLAY - Now animates because loadStage is triggered */}
      {isMobileOrTablet && !isMobileOverlayGone && (
        <div
          className={`fixed inset-0 z-[20002] bg-[#fcfcfc] flex flex-col items-center justify-center overflow-hidden transition-opacity duration-700 ${isAppLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
          onTransitionEnd={(e) => { if (e.target === e.currentTarget && isAppLoaded) setIsMobileOverlayGone(true); }}
        >
            <div className="relative w-full overflow-hidden h-20 flex items-center justify-center">
                <h1 className={`font-grandslang text-6xl text-charcoal transition-all duration-[1200ms] ${loadStage === 'initial' ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`} style={{ transitionTimingFunction: expoOut }}>
                    Daverose
                </h1>
            </div>
            <div className={`w-12 h-[1px] bg-charcoal/20 transition-all duration-1000 ${loadStage === 'splitting' || loadStage === 'settled' ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'}`} />
            <div className="relative w-full overflow-hidden h-20 flex items-center justify-center">
                <h1 className={`font-grandslang text-6xl text-charcoal transition-all duration-[1200ms] delay-150 ${loadStage === 'initial' ? '-translate-y-full opacity-0' : '-translate-y-0 opacity-100'}`} style={{ transitionTimingFunction: expoOut }}>
                    Tholley
                </h1>
            </div>
            
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 w-32 h-[1px] bg-charcoal/5">
                <div className={`h-full bg-charcoal transition-all duration-[2000ms] ease-out ${loadStage !== 'initial' ? 'w-full' : 'w-0'}`} />
            </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!isMobileMenuOpen}
        inert={isMobileMenuOpen ? undefined : ''}
        className={`fixed inset-0 z-[1000] bg-[#fcfcfc] transition-transform duration-700 ease-in-out ${isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'} flex flex-col items-center justify-center gap-8`}
      >
        {['Home', 'About', 'Projects', 'Stories', 'Contact'].map((item) => (
          <a key={item} href={`#${item.toLowerCase()}`} onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); scrollToSection(item.toLowerCase()); }} className="font-grandslang text-5xl text-charcoal">{item}</a>
        ))}
        <button onClick={() => setIsMobileMenuOpen(false)} className="absolute top-10 right-5 text-xs tracking-[3px] uppercase">Close</button>
      </div>

      <section id="home" className="relative hero-height bg-[#fcfcfc] overflow-hidden font-sans select-none">
        
        <nav className={`fixed bg-custom-background top-0 left-0 right-0 z-[100] py-8 px-5 xl:px-0 max-w-7xl mx-auto flex justify-between items-center pointer-events-none transition-opacity duration-1000 ${isAppLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            opacity: 1 - Math.min(movementProgress, 1), 
            transform: `translateX(${Math.min(movementProgress, 1) * 100}px)` 
          }}>
          <div className='overflow-hidden'>
             <div className="flex flex-col transition-transform duration-[1800ms] delay-[500ms]" style={{ transform: isIntroComplete ? 'translateY(0)' : 'translateY(110%)', transitionTimingFunction: expoOut }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-charcoal/10 backdrop-blur-md rounded-sm text-[8px] tracking-[2px] uppercase font-bold text-charcoal/80">
                  {currentGreetings[index]?.lang}
                </span>
                <span className="hidden min-[360px]:inline text-[10px] tracking-[1px] font-medium text-charcoal/60 italic">
                  — "{currentGreetings[index]?.meaning}"
                </span>
              </div>
              <div className="overflow-hidden">
                <span key={index} className="block text-xs tracking-[2px] uppercase font-black text-charcoal animate-[slideUp_0.6s_cubic-bezier(0.23, 1, 0.32, 1)]">
                  {currentGreetings[index]?.text}
                </span>
              </div>
            </div>
          </div>
          <div className='overflow-hidden pointer-events-auto'> 
            <div className="flex flex-col items-end transition-transform duration-[1800ms] delay-[500ms]" style={{ transitionTimingFunction: expoOut, transform: isIntroComplete ? 'translateY(0)' : 'translateY(110%)' }}>
              {isMobileOrTablet ? (
                <button onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" aria-expanded={isMobileMenuOpen} aria-controls="mobile-menu" className="flex flex-col gap-1.5 py-2">
                  <div className="w-8 h-[1px] bg-charcoal"></div>
                  <div className="w-8 h-[1px] bg-charcoal"></div>
                </button>
              ) : (
                <>
                  <span className="text-[12px] tracking-[5px] uppercase font-black text-black leading-none mb-1">Freetown - Accra</span>
                  <span className="text-[10px] tracking-[2px] text-black/80 uppercase">
                    <GmtClock />
                  </span> 
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Desktop Intro Animations (Conditioned to hide on mobile) */}
        {!isMobileOrTablet && (
          <>
            <div className="fixed inset-0 z-[10005] pointer-events-none flex items-center justify-center">
              <div className="relative flex items-center justify-center w-full h-40">
                {isIntroDaveroseActive && (
                  <div
                    className={`absolute z-[10007] transition-all ${isFastMode ? 'duration-[600ms]' : 'duration-[1600ms]'}`}
                    style={{
                      transitionTimingFunction: expoOut,
                      left: '50%',
                      transform: `translateX(${loadStage === 'initial' ? '-110%' : loadStage === 'splitting' ? '-120%' : '-50%'}) translateY(-50%) scale(${loadStage === 'settled' ? START_SCALE : 1})`,
                      top: '50%',
                    }}
                    onTransitionEnd={() => loadStage === 'settled' && setIsIntroDaveroseActive(false)}
                  >
                    <h1 className="font-grandslang text-[90px] text-charcoal leading-[1.1] whitespace-nowrap">Daverose</h1>
                  </div>
                )}
                <div
                  className={`absolute z-[10006] transition-all ${isFastMode ? 'duration-[600ms]' : 'duration-[1600ms]'}`}
                  style={{
                    transitionTimingFunction: expoOut,
                    left: '50%',
                    transform: `translateX(${loadStage === 'initial' ? '10%' : loadStage === 'splitting' ? '40%' : '400%'}) translateY(-50%)`,
                    top: '50%',
                    opacity: loadStage === 'settled' ? 0 : 1,
                  }}
                >
                  <h1 className="font-grandslang text-[90px] text-charcoal leading-[1.1] whitespace-nowrap">Tholley</h1>
                </div>
              </div>
            </div>

            {/* Positioned by the scroll handler above */}
            <div
              ref={fallingNameRef}
              aria-hidden="true"
              className="fixed top-1/2 left-1/2 z-[10002] pointer-events-none block opacity-0"
            >
              <h1 className="font-grandslang text-[90px] text-charcoal leading-[1.1] whitespace-nowrap">Daverose</h1>
            </div>
          </>
        )}

        {/* Unique Marquee Introduction (desktop: at phone width only a fragment shows) */}
        {!isMobileOrTablet && (
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden whitespace-nowrap transition-all duration-[2000ms] delay-[500ms]"
          style={{ 
            opacity: isIntroComplete ?  isMobileOrTablet ? 0.06 : 0.02 : 0,
            transform: isIntroComplete ? 'translateY(0) scale(1)' : 'translateY(100px) scale(0.95)',
            transitionTimingFunction: expoOut
          }}
        >
          <div className={`text-[35vw] md:text-[18vw] font-grandslang animate-[marquee_60s_linear_infinite] text-accent font-black`}>
            ENGINEER ADVOCATE DESIGNER ENGINEER ADVOCATE DESIGNER ENGINEER ADVOCATE DESIGNER
          </div>
        </div>
        )}

        {/* Mobile / tablet hero: the name stays on screen after the intro */}
        {isMobileOrTablet && (
          <div className="absolute inset-x-0 top-28 bottom-32 px-5 sm:px-10 flex flex-col justify-center">
            <p className="eyebrow mb-6 overflow-hidden">
              <span className="block transition-transform duration-[1400ms] delay-[100ms]" style={{ transitionTimingFunction: expoOut, transform: isIntroComplete ? 'translateY(0)' : 'translateY(110%)' }}>
                Freetown — Accra
              </span>
            </p>
            <h1 className="font-grandslang leading-[0.95] text-[min(20vw,10rem)]">
              <span className="block overflow-hidden pb-[0.08em]">
                <span className="block text-[#ff6347] transition-transform duration-[1400ms] delay-[200ms]" style={{ transitionTimingFunction: expoOut, transform: isIntroComplete ? 'translateY(0)' : 'translateY(110%)' }}>
                  Daverose
                </span>
              </span>
              <span className="block overflow-hidden pb-[0.08em]">
                <span className="block text-charcoal transition-transform duration-[1400ms] delay-[350ms]" style={{ transitionTimingFunction: expoOut, transform: isIntroComplete ? 'translateY(0)' : 'translateY(110%)' }}>
                  Tholley.
                </span>
              </span>
            </h1>

            <div
              className="mt-6 flex items-center gap-4 transition-opacity duration-1000 delay-[700ms]"
              style={{ opacity: isIntroComplete ? 1 : 0 }}
            >
              <span aria-hidden="true" className="h-px w-10 bg-charcoal/30" />
              <p className="font-grandslang text-3xl sm:text-4xl text-charcoal" aria-label="Engineer, advocate, designer">
                <RoleCycle />
              </p>
            </div>

            <a
              href="#projects"
              onClick={(e) => { e.preventDefault(); scrollToSection('projects'); }}
              className="group mt-10 inline-flex w-fit items-center gap-3 border-b border-charcoal/30 pb-1 text-[11px] uppercase tracking-[0.3em] font-semibold text-charcoal transition-opacity duration-1000 delay-[900ms]"
              style={{ opacity: isIntroComplete ? 1 : 0 }}
            >
              See the work
              <span aria-hidden="true" className="inline-block transition-transform duration-300 group-hover:translate-y-0.5">↓</span>
            </a>
          </div>
        )}

        {/* Bottom UI */}
        <div className="absolute bottom-10 md:bottom-24 left-0 right-0 px-5" style={{ opacity: 1 - Math.min(movementProgress, 1), transform: `translateX(${Math.min(movementProgress, 1) * 100}px)` }}>
          <div className="max-w-7xl mx-auto flex justify-between items-end">
            <div className="overflow-hidden">
              <div className="transition-transform duration-[1800ms] delay-[800ms]" style={{ transitionTimingFunction: expoOut, transform: isIntroComplete ? 'translateY(0)' : 'translateY(110%)' }}>
                <p className="eyebrow mb-1">Portfolio</p>
                <p className="text-[12px] tracking-[2px] text-charcoal/70">{new Date().getFullYear()} EDITION</p>
              </div>
            </div>
            <div className="text-right overflow-hidden">
              <div className="transition-transform duration-[1800ms] delay-[1000ms]" style={{ transitionTimingFunction: expoOut, transform: isIntroComplete ? 'translateY(0)' : 'translateY(110%)' }}>
                <p className="eyebrow mb-1">Available for</p>
                <div className="flex items-center gap-3 justify-end">
                  <div className="w-2 h-2 rounded-full bg-[#ff6347] animate-pulse"></div>
                  <p className="text-[12px] font-bold tracking-[2px] text-charcoal/70">Freelance Work</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="hidden absolute bottom-10 left-1/2 -translate-x-1/2 md:flex flex-col items-center gap-2 transition-opacity duration-1000" style={{ opacity: isIntroComplete ? (1 - Math.min(movementProgress, 1)) : 0 }}>
          <p className="text-sm tracking-[4px] uppercase text-charcoal/50">Scroll</p>
          <div className="w-[1px] h-16 bg-charcoal/20 relative overflow-hidden">
            <div className="w-[1px] bg-charcoal/60 absolute top-0" style={{ height: '50%', animation: 'scrollIndicator 2s ease-in-out infinite' }}></div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes slideUp { 0% { transform: translateY(100%); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes scrollIndicator { 0% { height: 0%; top: 0; } 50% { height: 100%; top: 0; } 100% { height: 0%; top: 100%; } }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      `}</style>
    </>
  );
};